<script setup>
import { ref, computed, onMounted } from 'vue';
import KanbanColumn from './KanbanColumn.vue';
import { useKanbanDragDrop } from 'dashboard/composables/kanban/useKanbanDragDrop';

const props = defineProps({
  funnel: {
    type: Object,
    required: true,
  },
  groupedConversations: {
    type: Object,
    required: true,
  },
  stageLoadingMore: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'cardClick',
  'cardMove',
  'cardRemove',
  'loadMore',
  'openConversation',
  'openCrm',
]);

// Handler for load more
const handleLoadMore = stageName => {
  emit('loadMore', stageName);
};

const boardRef = ref(null);

// Composable para drag and drop
const {
  dragOverColumn,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
} = useKanbanDragDrop();

// Computed para obter colunas ordenadas
const orderedColumns = computed(() => {
  if (!props.funnel?.stages) return [];

  return Object.entries(props.funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([stageName, stageConfig]) => {
      const conversations = props.groupedConversations[stageName] || [];
      return {
        name: stageName,
        ...stageConfig,
        conversations,
        hasMore: conversations.hasMore || false,
        totalCount: conversations.totalCount || conversations.length,
        isLoadingMore: props.stageLoadingMore[stageName] || false,
      };
    });
});

// Methods
const handleCardClick = conversation => {
  // Agora recebe a conversation completa para abrir o painel lateral
  emit('cardClick', conversation);
};

const handleCardRemove = conversation => {
  emit('cardRemove', conversation);
};

const handleOpenConversation = conversation => {
  emit('openConversation', conversation);
};

const handleOpenCrm = (conversation, contact) => {
  emit('openCrm', conversation, contact);
};

const handleCardMove = async moveData => {
  const { conversationId, fromColumn, toColumn } = moveData;

  if (fromColumn !== toColumn) {
    emit('cardMove', {
      conversationId,
      fromStage: fromColumn,
      toStage: toColumn,
    });
  }
};

// Setup drag and drop handlers
const onDragStart = (conversationId, fromColumn, event) => {
  handleDragStart({ conversationId, fromColumn }, event);
};

const onDragOver = (event, columnName) => {
  handleDragOver(event, columnName);
};

const onDragLeave = (event, columnName) => {
  handleDragLeave(event, columnName);
};

const onDrop = (event, columnName) => {
  const result = handleDrop(event, columnName);
  if (result) {
    handleCardMove(result);
  }
};

onMounted(() => {
  // Configurações adicionais se necessário
});
</script>

<template>
  <div
    ref="boardRef"
    class="kanban-board p-4 bg-n-background overflow-x-auto overflow-y-hidden h-full"
  >
    <div class="flex gap-3 h-full w-max">
      <KanbanColumn
        v-for="column in orderedColumns"
        :key="column.name"
        :stage-name="column.name"
        :stage-config="column"
        :conversations="column.conversations"
        :funnel-id="funnel.id"
        :is-drag-over="dragOverColumn === column.name"
        :has-more="column.hasMore"
        :total-count="column.totalCount"
        :is-loading-more="column.isLoadingMore"
        @card-click="handleCardClick"
        @card-remove="handleCardRemove"
        @open-conversation="handleOpenConversation"
        @open-crm="handleOpenCrm"
        @drag-start="onDragStart"
        @drag-over="onDragOver"
        @drag-leave="onDragLeave"
        @drop="onDrop"
        @load-more="handleLoadMore"
      />
    </div>
  </div>
</template>

<style scoped>
.kanban-board::-webkit-scrollbar {
  height: 6px;
}

.kanban-board::-webkit-scrollbar-track {
  background: transparent;
}

.kanban-board::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 3px;
}

.kanban-board::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}
</style>
